import { useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
} from '@mui/material'
import CachedImage from '../CachedImage'
import { inventoryService } from '../../services'
import { formatInventoryDateTime } from '../../utils/inventoryManagementUtils'

const formatAmount = (value) => {
  if (value == null || value === '') return '—'
  const num = Number(value)
  if (!Number.isFinite(num)) return '—'
  return num.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

function DetailField({ label, value, monospace = false, emphasize = false }) {
  return (
    <Box>
      <Typography variant="caption" sx={{ color: '#999' }}>
        {label}
      </Typography>
      <Typography
        sx={{
          fontWeight: emphasize ? 700 : 500,
          color: emphasize ? '#e90000' : 'inherit',
          fontFamily: monospace ? 'monospace' : 'inherit',
        }}
      >
        {value ?? '—'}
      </Typography>
    </Box>
  )
}

export default function ViewInventoryLotDialog({ open, item, onClose }) {
  const [lot, setLot] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!open || !item?.id) {
      setLot(null)
      setError(null)
      setLoading(false)
      return undefined
    }

    let cancelled = false
    setLoading(true)
    setError(null)

    inventoryService
      .getInventoryLotById(item.id)
      .then((data) => {
        if (cancelled) return
        setLot(data || null)
      })
      .catch((err) => {
        if (cancelled) return
        setLot(null)
        setError(err.response?.data?.message || 'Failed to load inventory details')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [open, item?.id])

  const product = lot?.adminProduct || item?.adminProduct || null
  const productName = product?.product_name || item?.adminProduct?.product_name || 'Inventory lot'
  const sku = lot?.sku || item?.sku || '—'
  const imageUrl =
    product?.image_url_portrait ||
    product?.image_url_banner ||
    product?.image_url_logo ||
    product?.additional_images?.[0] ||
    item?.adminProduct?.image_url_portrait ||
    item?.adminProduct?.image_url_banner ||
    item?.adminProduct?.image_url_logo ||
    (item?.adminProduct?.additional_images && item.adminProduct.additional_images[0])

  const sellingPrice =
    lot?.unit_cost ?? item?.adminProduct?.cost_price ?? null
  const mrp = lot?.unit_price ?? item?.adminProduct?.price ?? null
  const quantityOnHand = lot?.quantity_on_hand ?? item?.quantity_on_hand
  const locationName = lot?.location ?? item?.location ?? '—'
  const barcode = product?.barcode || item?.adminProduct?.barcode || '—'
  const brand = product?.brand || item?.adminProduct?.brand || '—'
  const notes = lot?.notes ?? item?.notes
  const updatedAt = lot?.updated_at || item?.updated_at

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 600, bgcolor: '#f8f9fb' }}>
        Inventory details
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={32} sx={{ color: '#e90000' }} />
          </Box>
        ) : (
          <>
            {error ? (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {error}
              </Alert>
            ) : null}

            <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'flex-start' }}>
              <CachedImage
                src={imageUrl}
                alt={productName}
                size={72}
                variant="rounded"
              />
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {productName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Article code: {sku}
                </Typography>
                {locationName !== '—' ? (
                  <Typography variant="body2" color="text.secondary">
                    Location: {locationName}
                  </Typography>
                ) : null}
              </Box>
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <DetailField label="Article code" value={sku} monospace />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DetailField label="Brand" value={brand} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DetailField label="Barcode" value={barcode} monospace />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DetailField label="Location" value={locationName} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DetailField
                  label="Quantity on hand"
                  value={
                    quantityOnHand != null
                      ? Math.floor(Number(quantityOnHand))
                      : '—'
                  }
                  emphasize
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DetailField
                  label="Selling Price"
                  value={formatAmount(sellingPrice)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DetailField label="MRP" value={formatAmount(mrp)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DetailField
                  label="Minimum stock level"
                  value={lot?.minimum_stock_level ?? item?.minimum_stock_level ?? '—'}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DetailField
                  label="Maximum stock level"
                  value={lot?.maximum_stock_level ?? item?.maximum_stock_level ?? '—'}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DetailField
                  label="Reorder point"
                  value={lot?.reorder_point ?? item?.reorder_point ?? '—'}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DetailField
                  label="Last updated"
                  value={formatInventoryDateTime(updatedAt)}
                />
              </Grid>
              {notes ? (
                <Grid item xs={12}>
                  <DetailField label="Notes" value={notes} />
                </Grid>
              ) : null}
            </Grid>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} variant="contained" sx={{ bgcolor: '#e90000', '&:hover': { bgcolor: '#c00000' } }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  )
}
